import { computed, ref } from "vue";
import route from "ziggy-js";
import type {
    IAiChatDetail,
    IAiChatListItem,
    IAiChatMessage,
} from "@/types/AiChat/IAiChat";
import type { IAiSkillAttachment } from "@/types/AiChat/IAiSkill";
import type { IAiPageContext } from "@/composables/useAiPageContext";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

interface ISavedContentItem {
    id: number;
    title: string;
    summary: string | null;
    created_at: string | null;
}

interface ISavedContentDetail extends ISavedContentItem {
    content: string;
}

const POLL_INTERVAL_MS = 2500;
// ~3 minutes. A reply job lives at most ~75 s server-side; polling past this
// point means the job was lost, so we stop instead of hammering the API.
const MAX_PENDING_POLLS = 72;

/**
 * Encapsulates the floating widget's data: chat list, the active conversation,
 * saved content and message sending — all over the JSON widget endpoints, with
 * optimistic user bubbles and polling while a reply is being generated.
 *
 * Every load is guarded: on failure it keeps the previous data, surfaces a
 * notification and stops any polling, so a transient error never leaves a
 * silently empty panel.
 */
export function useAiWidgetChat() {
    const { t } = useTranslations();

    const chats = ref<IAiChatListItem[]>([]);
    const activeChat = ref<IAiChatDetail | null>(null);
    const pending = ref(false);
    const sending = ref(false);
    const chatsLoading = ref(false);
    const savedLoading = ref(false);
    const savedItems = ref<ISavedContentItem[]>([]);
    const savedDetail = ref<ISavedContentDetail | null>(null);

    let tempId = -1;
    const optimistic = ref<IAiChatMessage[]>([]);

    const messages = computed<IAiChatMessage[]>(() => [
        ...(activeChat.value?.messages ?? []),
        ...optimistic.value,
    ]);

    let pollTimer: number | undefined;
    let pollCount = 0;

    const stopPolling = () => {
        if (pollTimer !== undefined) {
            window.clearInterval(pollTimer);
            pollTimer = undefined;
        }
        pollCount = 0;
    };

    const notifyError = () => {
        create({ title: t("vue.ai_widget_load_error"), type: "error" });
    };

    const notifyReplyTimeout = () => {
        create({ title: t("vue.ai_chat_reply_timeout"), type: "error" });
    };

    const loadChats = async (): Promise<void> => {
        chatsLoading.value = true;
        try {
            const { data } = await axios.get(route("ai-chat.api.chats"));
            chats.value = data.chats ?? [];
        } catch {
            notifyError();
        } finally {
            chatsLoading.value = false;
        }
    };

    const loadChat = async (chatId: number): Promise<void> => {
        try {
            const { data } = await axios.get(
                route("ai-chat.api.chat", { chat: chatId }),
            );
            activeChat.value = data.chat ?? null;
            pending.value = data.pending === true;
            if (pending.value === true) {
                startPolling();
            } else {
                stopPolling();
            }
        } catch {
            stopPolling();
            notifyError();
        }
    };

    const startPolling = () => {
        if (pollTimer !== undefined || activeChat.value === null) {
            return;
        }
        pollTimer = window.setInterval(() => {
            if (activeChat.value === null) {
                stopPolling();
                return;
            }
            pollCount += 1;
            if (pollCount > MAX_PENDING_POLLS) {
                stopPolling();
                pending.value = false;
                notifyReplyTimeout();
                return;
            }
            void loadChat(activeChat.value.id);
        }, POLL_INTERVAL_MS);
    };

    const openChat = async (chatId: number): Promise<void> => {
        optimistic.value = [];
        await loadChat(chatId);
    };

    const backToList = (): void => {
        stopPolling();
        activeChat.value = null;
        optimistic.value = [];
    };

    const pushOptimistic = (
        message: string,
        image: File | null,
        imageUrl: string | null,
        skill: IAiSkillAttachment | null,
    ): void => {
        optimistic.value = [
            ...optimistic.value,
            {
                id: tempId--,
                type: "user",
                content: message !== "" ? message : null,
                tool_name: null,
                created_at: null,
                attachment:
                    image !== null && imageUrl !== null
                        ? { url: imageUrl, filename: image.name }
                        : null,
                skill,
            },
        ];
    };

    // With an image the request must be multipart; the page context then travels
    // as a JSON string (the backend decodes it before validation). Without an
    // image we keep the plain JSON body.
    const buildBody = (
        message: string,
        pageContext: IAiPageContext | null,
        image: File | null,
        skillId: number | null,
    ): FormData | Record<string, unknown> => {
        if (image === null) {
            return { message, page_context: pageContext, skill_id: skillId };
        }

        const body = new FormData();
        if (message !== "") {
            body.append("message", message);
        }
        body.append("image", image);
        if (pageContext !== null) {
            body.append("page_context", JSON.stringify(pageContext));
        }
        if (skillId !== null) {
            body.append("skill_id", String(skillId));
        }
        return body;
    };

    const sendMessage = async (
        message: string,
        pageContext: IAiPageContext | null,
        image: File | null = null,
        skill: IAiSkillAttachment | null = null,
    ): Promise<void> => {
        sending.value = true;
        const localImageUrl =
            image !== null ? URL.createObjectURL(image) : null;
        pushOptimistic(message, image, localImageUrl, skill);

        const skillId = skill?.id ?? null;

        try {
            const body = buildBody(message, pageContext, image, skillId);

            if (activeChat.value === null) {
                const { data } = await axios.post(
                    route("ai-chat.api.chats.store"),
                    body,
                );
                await loadChats();
                await loadChat(data.chat_id);
                optimistic.value = [];
            } else {
                await axios.post(
                    route("ai-chat.api.messages.store", {
                        chat: activeChat.value.id,
                    }),
                    body,
                );
                optimistic.value = [];
                pending.value = true;
                await loadChat(activeChat.value.id);
            }
        } catch {
            optimistic.value = [];
            notifyError();
        } finally {
            sending.value = false;
            if (localImageUrl !== null) {
                URL.revokeObjectURL(localImageUrl);
            }
        }
    };

    const loadSaved = async (search: string): Promise<void> => {
        savedLoading.value = true;
        try {
            const { data } = await axios.get(
                route("ai-chat.api.saved-content"),
                { params: { q: search } },
            );
            savedItems.value = data.items ?? [];
        } catch {
            notifyError();
        } finally {
            savedLoading.value = false;
        }
    };

    const openSaved = async (id: number): Promise<void> => {
        try {
            const { data } = await axios.get(
                route("ai-chat.api.saved-content.detail", { savedContent: id }),
            );
            savedDetail.value = data.content ?? null;
        } catch {
            notifyError();
        }
    };

    return {
        chats,
        activeChat,
        messages,
        pending,
        sending,
        chatsLoading,
        savedLoading,
        savedItems,
        savedDetail,
        loadChats,
        openChat,
        backToList,
        sendMessage,
        loadSaved,
        openSaved,
        stopPolling,
    };
}
